import { useCallback, useRef, useState } from "react";
import PropTypes from "prop-types";
import { useSelector } from "react-redux";
import { caseTransformingAxios } from "@shared/v2/caseTransformingAxios";
import Tooltip from "@shared/v2/Tooltip/Tooltip";
import MarketerOnboardingTooltip from "@shared/v2/MarketerOnboardingTooltip";
import { HandwrittenCardModal } from "@shared/v2/HandwrittenCard";
import useIframeMessageHandler from "@shared/v2/HandwrittenCard/useIframeMessageHandler";
import { isUSCountry } from "@shared/v2/utils";
import { useCurrentUser, useFeatureFlags } from "../../../reducers/layoutReducer/selectors";
import MailingListOverviewModal from "./MailingListOverviewModal";

const HandwrittenCardOption = ({ isOnboarded = false, onClick = () => {} }) => (
  <button
    type="button"
    id="mass-send-handwritten-card"
    className={`tw-clear-both tw-flex tw-w-[251px] tw-items-center tw-justify-between tw-border-0 tw-bg-transparent tw-px-[10px] tw-py-[8px] tw-text-left tw-font-normal tw-normal-case ${!isOnboarded ? "!tw-cursor-default !tw-bg-[#f0f0f0] !tw-text-[#cccccc]" : "tw-text-gray-dark hover:tw-bg-gray-5 hover:tw-text-gray-dark"}`}
    onClick={() => isOnboarded && onClick()}
  >
    <span>Handwritten Card</span>
    <span className="tw-ml-auto tw-inline-flex tw-h-[20px] tw-items-center tw-rounded-[4px] tw-bg-[#59c4c4] tw-px-[4px] tw-py-[2px] tw-text-[9.319px] tw-font-extrabold tw-uppercase tw-leading-none tw-text-white">
      New
    </span>
  </button>
);

HandwrittenCardOption.propTypes = {
  isOnboarded: PropTypes.bool,
  onClick: PropTypes.func,
};

const getSelectedPersonIds = () => Brivity.PeoplePage.grid().multiSelect().getSelectedObjectIds();

const isAllSelected = () => document.getElementById("is-all-selected")?.value === "true";

const getPayload = () => (isAllSelected() ? { selectAll: true } : { personIds: getSelectedPersonIds() });

const MassSendHandwrittenCard = () => {
  // Overview modal state
  const [isOverviewOpen, setIsOverviewOpen] = useState(false);
  const [isValidating, setIsValidating] = useState(false);
  const [validationData, setValidationData] = useState(null);

  // Compose modal state
  const [isComposeLoading, setIsComposeLoading] = useState(false);
  const [isComposeOpen, setIsComposeOpen] = useState(false);
  const [iframeUrl, setIframeUrl] = useState(null);
  const [billingPageUrl, setBillingPageUrl] = useState(null);
  const [composeError, setComposeError] = useState(null);
  const iframeRef = useRef(null);

  const featureFlags = useFeatureFlags();
  const currentUser = useCurrentUser();
  const isAdmin = currentUser?.account?.isAdmin;
  const isAgent = currentUser?.account?.isAgent;
  const isUSAccount = isUSCountry(currentUser?.account?.country);
  const marketerOnboardingStatus = useSelector((state) => state.personDetail.marketerOnboardingStatus);

  const { team_onboarded: teamOnboarded, agent_onboarded: agentOnboarded } =
    marketerOnboardingStatus?.data || {};

  const isOnboarded = (isAdmin && teamOnboarded) || (isAgent && teamOnboarded && agentOnboarded) || false;

  const resetState = useCallback(() => {
    setIsOverviewOpen(false);
    setIsValidating(false);
    setValidationData(null);
    setIsComposeLoading(false);
    setIsComposeOpen(false);
    setIframeUrl(null);
    setBillingPageUrl(null);
    setComposeError(null);
  }, []);

  useIframeMessageHandler({ iframeUrl, iframeRef, billingPageUrl, onClose: resetState });

  // Step 1: Open overview modal and validate
  const handleClick = async () => {
    setIsOverviewOpen(true);
    setIsValidating(true);
    setValidationData(null);

    try {
      const response = await caseTransformingAxios.post("/api/v4/inked/batch_validate", getPayload());
      setValidationData(response.data);
    } catch (err) {
      setValidationData({ error: err.response?.data?.error || "Failed to validate recipients" });
    } finally {
      setIsValidating(false);
    }
  };

  // Step 2: Start compose flow
  const handleNext = async () => {
    setIsOverviewOpen(false);
    setIsComposeOpen(true);
    setIsComposeLoading(true);
    setComposeError(null);

    try {
      const response = await caseTransformingAxios.post("/api/v4/inked/batch_iframe_url", getPayload());

      if (response.data.iframeUrl) {
        setIframeUrl(response.data.iframeUrl);
        setBillingPageUrl(response.data.billingPageUrl);
      } else {
        setComposeError(response.data.error || "Failed to start handwritten card campaign");
      }
    } catch (err) {
      const errorMessage = err.response?.data?.error || "Failed to start handwritten card campaign";
      setComposeError(errorMessage);
    } finally {
      setIsComposeLoading(false);
    }
  };

  if (!featureFlags?.sendHandwrittenCard || !isUSAccount) {
    return null;
  }

  return (
    <>
      <li className="divider" />
      <li>
        {!isOnboarded ? (
          <Tooltip
            innerClassName="tw-max-w-[250px]"
            placement="left"
            multiline
            content={<MarketerOnboardingTooltip />}
            trigger={<HandwrittenCardOption isOnboarded={isOnboarded} onClick={handleClick} />}
          />
        ) : (
          <HandwrittenCardOption isOnboarded={isOnboarded} onClick={handleClick} />
        )}
      </li>

      <MailingListOverviewModal
        show={isOverviewOpen}
        onHide={resetState}
        onNext={handleNext}
        isLoading={isValidating}
        validationData={validationData}
      />

      <HandwrittenCardModal
        show={isComposeOpen}
        onHide={resetState}
        isLoading={isComposeLoading}
        iframeUrl={iframeUrl}
        iframeRef={iframeRef}
        error={composeError}
      />
    </>
  );
};

export default MassSendHandwrittenCard;
